// fireflies.js
import * as THREE from 'three';

/**
 * Create a group of "fireflies" with random positions and velocities.
 */
export function createFireflyGroup(textureURL, count) {
  const loader = new THREE.TextureLoader();
  const texture = loader.load(textureURL);

  const positions = new Float32Array(count * 3);
  const velocities = new Float32Array(count * 3);
  const swirlAngles = new Float32Array(count);
  const colors = new Float32Array(count * 3);

  for (let i = 0; i < count; i++) {
    const i3 = i * 3;
    positions[i3] = (Math.random() - 0.5) * 50;
    positions[i3 + 1] = (Math.random() - 0.5) * 50;
    positions[i3 + 2] = -10 - Math.random() * 140;

    velocities[i3] = (Math.random() - 0.5) * 2.5;
    velocities[i3 + 1] = (Math.random() - 0.5) * 2.5;
    velocities[i3 + 2] = (Math.random() - 0.5) * 2.5;

    swirlAngles[i] = Math.random() * Math.PI * 2;

    colors[i3] = 1.0;
    colors[i3 + 1] = 1.0;
    colors[i3 + 2] = 1.0;
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));

  const material = new THREE.PointsMaterial({
    size: 2.0,
    map: texture,
    transparent: true,
    alphaTest: 0.1,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    vertexColors: true,
    opacity: 0.6,
  });

  const points = new THREE.Points(geometry, material);
  return {
    geometry,
    material,
    points,
    velocities,
    swirlAngles,
    count,
  };
}

/**
 * Update an existing group of fireflies: movement, swirl around mouse, brightness, etc.
 */
export function updateFireflies(group, delta, mouseX, mouseY) {
  const { geometry, velocities, swirlAngles, count } = group;
  const positions = geometry.attributes.position.array;
  const colors = geometry.attributes.color.array;

  for (let i = 0; i < count; i++) {
    const i3 = i * 3;

    positions[i3] += velocities[i3] * delta;
    positions[i3 + 1] += velocities[i3 + 1] * delta;

    // Wrap around
    if (positions[i3] > 125) positions[i3] = -125;
    if (positions[i3] < -125) positions[i3] = 125;
    if (positions[i3 + 1] > 125) positions[i3 + 1] = -125;
    if (positions[i3 + 1] < -125) positions[i3 + 1] = 125;

    // Swirl / pull
    const dx = mouseX - positions[i3];
    const dy = mouseY - positions[i3 + 1];
    const dist = Math.sqrt(dx * dx + dy * dy);
    const swirlRadius = 8;
    const pullRadius = 25;
    const bounceRadius = 2;

    if (dist < swirlRadius) {
      swirlAngles[i] += 1.0 * delta;
      const swirlDist = dist;
      const angle = swirlAngles[i];
      const orbitX = mouseX + Math.cos(angle) * swirlDist;
      const orbitY = mouseY + Math.sin(angle) * swirlDist;

      const approachFactor = 0.25;
      positions[i3] += (orbitX - positions[i3]) * approachFactor * delta;
      positions[i3 + 1] += (orbitY - positions[i3 + 1]) * approachFactor * delta;

      if (dist < bounceRadius) {
        velocities[i3] *= -0.5;
        velocities[i3 + 1] *= -0.5;
      }
    } else if (dist < pullRadius) {
      const pullFactor = 0.28;
      velocities[i3] += dx * pullFactor * delta;
      velocities[i3 + 1] += dy * pullFactor * delta;
    }

    // Brightness effect
    let brightness = 1.0;
    if (dist < pullRadius) {
      brightness = 2.2;
    }
    colors[i3 + 0] = brightness;
    colors[i3 + 1] = brightness;
    colors[i3 + 2] = brightness;
  }

  geometry.attributes.position.needsUpdate = true;
  geometry.attributes.color.needsUpdate = true;
}

/**
 * Utility to convert screen coords to "firefly space" near the center of the screen.
 */
export function screenToFireflySpace(x, y, width, height, range = 25) {
  const worldX = (x / width - 0.5) * 2 * range;
  const worldY = (0.5 - y / height) * 2 * range;
  return [worldX, worldY];
}
